import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../lib/firebase";
import { useAuth } from "../contexts/AuthContext";
import { useUser } from "../lib/api-client";
import { clsx } from "clsx";

const loginSchema = z.object({
  email: z.email(),
  password: z.string(),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export function Login() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { data: user, isLoading: isUserLoading } = useUser();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  useEffect(() => {
    if (isAuthenticated && user?.isAdmin) {
      void navigate("/");
    }
  }, [isAuthenticated, user, navigate]);

  const onSubmit = async (data: LoginFormValues) => {
    setIsLoading(true);
    setError(null);

    try {
      await signInWithEmailAndPassword(auth, data.email, data.password);
    } catch {
      // Error is already displayed to user via setError
      setError("Invalid email or password");
      await auth.signOut();
    } finally {
      setIsLoading(false);
    }
  };

  if (isUserLoading && isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-600"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading profile...</p>
        </div>
      </div>
    );
  }

  // Access Denied View
  if (isAuthenticated && user && !user.isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="bg-red-50 border-b border-red-100 p-6 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4 text-red-600">
                <i className="pi pi-lock text-3xl"></i>
              </div>
              <h1 className="text-2xl font-bold text-red-900">Access Denied</h1>
              <p className="mt-2 text-red-700 font-medium">
                Your account does not have administrative privileges.
              </p>
            </div>

            <div className="p-8 space-y-4">
              <p className="text-slate-600 text-center text-sm leading-relaxed">
                If you believe this is an error, please contact the system
                administrator to request access to the booking management panel.
              </p>
              <div className="pt-4">
                <Button
                  label="Sign Out"
                  onClick={() => {
                    void auth.signOut();
                  }}
                  className="w-full h-12 bg-black hover:bg-slate-900 border-none text-white text-base font-bold tracking-widest transition-all shadow-md active:scale-[0.98]"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
          <div className="bg-blue-50 border-b border-blue-100 p-6 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4 text-blue-600">
              <i className="pi pi-compass text-3xl"></i>
            </div>
            <h1 className="text-2xl font-extrabold text-blue-900 tracking-tight">
              NLHSC Booking
            </h1>
            <p className="mt-1 text-blue-700 font-bold text-sm tracking-widest uppercase">
              Sign In
            </p>
          </div>

          <div className="p-8">
            <form
              onSubmit={(e) => {
                void handleSubmit(onSubmit)(e);
              }}
              className="space-y-5"
            >
              {error && (
                <Message
                  severity="error"
                  text={error}
                  className="w-full justify-start p-3"
                />
              )}

              <div className="space-y-1.5">
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-slate-700"
                >
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  {...register("email")}
                  className={clsx(
                    "block w-full h-11 px-4 text-base border rounded-md transition-all focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none shadow-sm",
                    {
                      "border-red-500 bg-red-50": errors.email,
                      "border-slate-300": !errors.email,
                    },
                  )}
                />
                {errors.email && (
                  <small className="text-red-500 font-medium text-xs mt-1 block">
                    {errors.email.message}
                  </small>
                )}
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="password"
                  className="block text-sm font-semibold text-slate-700"
                >
                  Password
                </label>
                <div className="relative w-full h-11">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    {...register("password")}
                    className={clsx(
                      "block w-full h-full pl-4 pr-12 text-base border rounded-md transition-all focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none shadow-sm",
                      {
                        "border-red-500 bg-red-50": errors.password,
                        "border-slate-300": !errors.password,
                      },
                    )}
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowPassword(!showPassword);
                    }}
                    className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-400 hover:text-slate-600 transition-colors z-10"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <i
                      className={clsx("pi text-lg", {
                        "pi-eye-slash": showPassword,
                        "pi-eye": !showPassword,
                      })}
                    ></i>
                  </button>
                </div>
                {errors.password && (
                  <small className="text-red-500 font-medium text-xs mt-1 block">
                    {errors.password.message}
                  </small>
                )}
              </div>

              <div className="pt-2">
                <Button
                  label={isLoading ? "Signing in..." : "Sign In"}
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-12 bg-black hover:bg-slate-900 border-none text-white text-base font-bold tracking-widest transition-all shadow-lg active:scale-[0.98]"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
